import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  LockOpen as LockOpenIcon,
  LockReset as LockResetIcon,
  ContentCopy as CopyIcon,
  ShieldMoon as ShieldMoonIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import DataTable, { type Column } from "@/components/DataTable";
import { useSystemAdmins } from "./hooks/useSystemAdmins";
import type { SystemAdmin } from "@/types";

interface CreateFormData {
  email: string;
  first_name: string;
  last_name: string;
  generate_password: boolean;
  password: string;
  show_password: boolean;
}

interface EditFormData {
  first_name: string;
  last_name: string;
  is_active: boolean;
}

const emptyCreateForm: CreateFormData = {
  email: "",
  first_name: "",
  last_name: "",
  generate_password: true,
  password: "",
  show_password: false,
};

const SystemAdmins: React.FC = () => {
  // Dialog states
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<SystemAdmin | null>(null);
  const [resetConfirmAdmin, setResetConfirmAdmin] = useState<SystemAdmin | null>(null);
  const [deleteConfirmAdmin, setDeleteConfirmAdmin] = useState<SystemAdmin | null>(null);
  const [unlockConfirmAdmin, setUnlockConfirmAdmin] = useState<SystemAdmin | null>(null);
  const [mfaResetAdmin, setMfaResetAdmin] = useState<SystemAdmin | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Form states
  const [createForm, setCreateForm] = useState<CreateFormData>(emptyCreateForm);
  const [editForm, setEditForm] = useState<EditFormData>({ first_name: "", last_name: "", is_active: true });

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  const {
    admins,
    isLoading,
    createAdmin,
    isCreating,
    updateAdmin,
    isUpdating,
    deleteAdminAsync,
    isDeleting,
  } = useSystemAdmins();

  const passwordErrors = useMemo(() => {
    if (createForm.generate_password) return [];
    const errors: string[] = [];
    if (createForm.password.length < 12) errors.push("At least 12 characters");
    if (!/[a-z]/.test(createForm.password)) errors.push("One lowercase letter");
    if (!/[A-Z]/.test(createForm.password)) errors.push("One uppercase letter");
    if (!/[0-9]/.test(createForm.password)) errors.push("One number");
    if (!/[!@#$%^&*]/.test(createForm.password)) errors.push("One special character (!@#$%^&*)");
    return errors;
  }, [createForm.generate_password, createForm.password]);

  // --- Create ---
  const handleOpenCreate = () => {
    setCreateForm(emptyCreateForm);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    try {
      const created = await createAdmin({
        email: createForm.email,
        first_name: createForm.first_name || undefined,
        last_name: createForm.last_name || undefined,
        generate_password: createForm.generate_password,
        password: createForm.generate_password ? undefined : createForm.password,
      });
      setCreateOpen(false);
      setSnackbar({ open: true, message: "Admin created", severity: "success" });
      if (created.generated_password) {
        setGeneratedPassword(created.generated_password);
        setCopied(false);
        setPasswordDialogOpen(true);
      }
    } catch (err: unknown) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : "Request failed",
        severity: "error",
      });
    }
  };

  const isCreateValid =
    createForm.email.trim().length > 0 &&
    (createForm.generate_password || (passwordErrors.length === 0 && createForm.password.length > 0));

  // --- Edit ---
  const handleOpenEdit = (admin: SystemAdmin) => {
    setEditingAdmin(admin);
    setEditForm({
      first_name: admin.first_name || "",
      last_name: admin.last_name || "",
      is_active: admin.is_active,
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingAdmin) return;
    try {
      await updateAdmin({
        id: editingAdmin.id,
        payload: {
          first_name: editForm.first_name || undefined,
          last_name: editForm.last_name || undefined,
          is_active: editForm.is_active,
        },
      });
      setEditOpen(false);
      setEditingAdmin(null);
      setSnackbar({ open: true, message: "Admin updated", severity: "success" });
    } catch (err: unknown) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : "Request failed",
        severity: "error",
      });
    }
  };

  // --- Reset Password ---
  const handleResetPassword = async () => {
    if (!resetConfirmAdmin) return;
    try {
      const updated = await updateAdmin({
        id: resetConfirmAdmin.id,
        payload: { reset_password: true },
      });
      setResetConfirmAdmin(null);
      if (updated.generated_password) {
        setGeneratedPassword(updated.generated_password);
        setCopied(false);
        setPasswordDialogOpen(true);
      }
      setSnackbar({ open: true, message: "Password reset successfully", severity: "success" });
    } catch (err: unknown) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : "Failed to reset password",
        severity: "error",
      });
    }
  };

  // --- Delete ---
  const handleDelete = async () => {
    if (!deleteConfirmAdmin) return;
    try {
      await deleteAdminAsync(deleteConfirmAdmin.id);
      setDeleteConfirmAdmin(null);
      setSnackbar({ open: true, message: "Admin deleted", severity: "success" });
    } catch (err: unknown) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : "Failed to delete admin",
        severity: "error",
      });
    }
  };

  // --- Reset MFA ---
  const handleResetMfa = async () => {
    if (!mfaResetAdmin) return;
    try {
      await updateAdmin({
        id: mfaResetAdmin.id,
        payload: { reset_mfa: true },
      });
      setMfaResetAdmin(null);
      setSnackbar({ open: true, message: "MFA reset for user. They will set it up again on next sign-in.", severity: "success" });
    } catch (err: unknown) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : "Failed to reset MFA",
        severity: "error",
      });
    }
  };

  // --- Unlock ---
  const handleUnlock = async () => {
    if (!unlockConfirmAdmin) return;
    try {
      await updateAdmin({
        id: unlockConfirmAdmin.id,
        payload: { unlock_account: true },
      });
      setUnlockConfirmAdmin(null);
      setSnackbar({ open: true, message: "Account unlocked", severity: "success" });
    } catch (err: unknown) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : "Failed to unlock account",
        severity: "error",
      });
    }
  };

  // --- Copy ---
  const handleCopy = async () => {
    if (!generatedPassword) return;
    await navigator.clipboard.writeText(generatedPassword);
    setCopied(true);
  };

  const columns: Column<SystemAdmin>[] = [
    {
      id: "name",
      label: "Name",
      render: (row) => {
        const name =
          row.first_name || row.last_name
            ? `${row.first_name || ""} ${row.last_name || ""}`.trim()
            : "-";
        return (
          <Typography variant="body2" fontWeight={700}>
            {name}
          </Typography>
        );
      },
    },
    {
      id: "email",
      label: "Email",
      render: (row) => (
        <Typography
          variant="body2"
          sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: "0.8125rem" }}
        >
          {row.email}
        </Typography>
      ),
    },
    {
      id: "status",
      label: "Status",
      render: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Chip
            size="small"
            label={row.is_active ? "Active" : "Inactive"}
            sx={{
              bgcolor: row.is_active ? "#DCFCE7" : "#FEE2E2",
              color: row.is_active ? "#166534" : "#991B1B",
              fontWeight: 700,
              letterSpacing: "0.04em",
            }}
          />
          {row.account_locked && (
            <Chip
              size="small"
              label="Locked"
              sx={{
                bgcolor: "#FEF3C7",
                color: "#92400E",
                fontWeight: 700,
                letterSpacing: "0.04em",
              }}
            />
          )}
          {row.has_password === false && (
            <Chip
              size="small"
              label="SSO"
              sx={{
                bgcolor: "#DBEAFE",
                color: "#1E40AF",
                fontWeight: 700,
                letterSpacing: "0.04em",
              }}
            />
          )}
        </Stack>
      ),
    },
    {
      id: "mfa",
      label: "2FA",
      render: (row) => (
        <Typography
          variant="body2"
          sx={{
            color: row.mfa_enabled ? "success.main" : "text.secondary",
            fontWeight: 600,
          }}
        >
          {row.mfa_enabled ? "On" : "Off"}
        </Typography>
      ),
    },
    {
      id: "lastLogin",
      label: "Last Login",
      render: (row) =>
        row.last_login ? (
          <Typography variant="body2">
            {new Date(row.last_login).toLocaleString(undefined, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        ),
    },
    {
      id: "actions",
      label: "Actions",
      align: "right",
      render: (row) => (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => setDeleteConfirmAdmin(row)}
              disabled={isDeleting}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.account_locked && (
            <Tooltip title="Unlock Account">
              <IconButton
                size="small"
                color="warning"
                onClick={() => setUnlockConfirmAdmin(row)}
                disabled={isUpdating}
              >
                <LockOpenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {row.mfa_enabled && (
            <Tooltip title="Reset MFA">
              <IconButton
                size="small"
                onClick={() => setMfaResetAdmin(row)}
                disabled={isUpdating}
              >
                <ShieldMoonIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {row.has_password !== false && (
            <Tooltip title="Reset Password">
              <IconButton
                size="small"
                onClick={() => setResetConfirmAdmin(row)}
                disabled={isUpdating}
              >
                <LockResetIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleOpenEdit(row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Platform Admins
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage platform administrators with full system access.
          </Typography>
        </Box>
        <Button variant="contained" onClick={handleOpenCreate}>
          Add Platform Admin
        </Button>
      </Stack>

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add New Platform Admin</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              fullWidth
              required
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="First Name"
                value={createForm.first_name}
                onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
                fullWidth
              />
              <TextField
                label="Last Name"
                value={createForm.last_name}
                onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })}
                fullWidth
              />
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={createForm.generate_password}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      generate_password: e.target.checked,
                      password: e.target.checked ? "" : createForm.password,
                    })
                  }
                />
              }
              label="Generate random password"
            />
            {!createForm.generate_password && (
              <>
                <TextField
                  label="Temporary Password"
                  type={createForm.show_password ? "text" : "password"}
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  fullWidth
                  error={passwordErrors.length > 0 && createForm.password.length > 0}
                  helperText={
                    createForm.password.length === 0
                      ? ""
                      : passwordErrors.length > 0
                        ? `Missing: ${passwordErrors.join(", ")}`
                        : "Meets policy"
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={createForm.show_password}
                      onChange={(e) => setCreateForm({ ...createForm, show_password: e.target.checked })}
                    />
                  }
                  label="Show password"
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!isCreateValid || isCreating}
          >
            {isCreating ? <CircularProgress size={24} color="inherit" /> : "Add Admin"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => { setEditOpen(false); setEditingAdmin(null); }} fullWidth maxWidth="sm">
        <DialogTitle>Edit Platform Admin</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              value={editingAdmin?.email ?? ""}
              fullWidth
              disabled
              helperText="Email cannot be changed"
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="First Name"
                value={editForm.first_name}
                onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                fullWidth
              />
              <TextField
                label="Last Name"
                value={editForm.last_name}
                onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                fullWidth
              />
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.is_active}
                  onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setEditOpen(false); setEditingAdmin(null); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={isUpdating}
          >
            {isUpdating ? <CircularProgress size={24} color="inherit" /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Confirmation Dialog */}
      <Dialog
        open={!!resetConfirmAdmin}
        onClose={() => setResetConfirmAdmin(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to reset the password for{" "}
            <strong>{resetConfirmAdmin?.email}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            A new temporary password will be generated. The current password will stop working immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResetConfirmAdmin(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleResetPassword}
            disabled={isUpdating}
          >
            {isUpdating ? <CircularProgress size={24} color="inherit" /> : "Reset Password"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset MFA Confirmation Dialog */}
      <Dialog
        open={!!mfaResetAdmin}
        onClose={() => setMfaResetAdmin(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reset MFA</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to reset MFA for{" "}
            <strong>{mfaResetAdmin?.email}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Their current authenticator and recovery codes will be invalidated. If MFA is
            enforced they will be required to set it up again on next sign-in, otherwise they
            can re-enable it from their Security page.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMfaResetAdmin(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleResetMfa}
            disabled={isUpdating}
          >
            {isUpdating ? <CircularProgress size={24} color="inherit" /> : "Reset MFA"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirmAdmin}
        onClose={() => setDeleteConfirmAdmin(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Platform Admin</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete{" "}
            <strong>{deleteConfirmAdmin?.email}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. The admin will lose all access immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmAdmin(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? <CircularProgress size={24} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unlock Confirmation Dialog */}
      <Dialog
        open={!!unlockConfirmAdmin}
        onClose={() => setUnlockConfirmAdmin(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Unlock Account</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to unlock the account for{" "}
            <strong>{unlockConfirmAdmin?.email}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will reset failed login attempts and allow the user to log in again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setUnlockConfirmAdmin(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleUnlock}
            disabled={isUpdating}
          >
            {isUpdating ? <CircularProgress size={24} color="inherit" /> : "Unlock"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generated Password Dialog */}
      <Dialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Temporary Password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Password"
              value={generatedPassword || ""}
              fullWidth
              slotProps={{ input: { readOnly: true } }}
            />
            <Button
              variant="outlined"
              startIcon={<CopyIcon />}
              onClick={handleCopy}
              color={copied ? "success" : "primary"}
            >
              {copied ? "Copied!" : "Copy Password"}
            </Button>
            <Alert severity="warning" variant="outlined">
              Share this with the user. It will not be shown again.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      <DataTable
        columns={columns}
        data={admins}
        keyExtractor={(row) => row.id}
        loading={isLoading}
        emptyMessage="No platform admins found"
        emptyDescription="Add your first platform admin to get started."
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SystemAdmins;
